import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import httpRequest from "../../services/httpRequest";
import AppUtil from "utilities/AppUtil";
import snakecaseKeys from 'snakecase-keys';
import { getMe, saveAuthToken } from "./auth";
import { showToast } from "./toast";
import i18next from "i18next";
import AppConfig from "config/AppConfig";
import StorageHelper from "utilities/StorageHelper";

const { APPROVAL_STATUS } = AppConfig;
const _urlPrefix = '/company';
const t = i18next.getFixedT();

export const fetchCompanyListPaging = createAsyncThunk(
  "company/fetchCompanyListPaging",
  async (params) => {
    const result = await httpRequest({
      url: _urlPrefix,
      method: "GET",
      params: AppUtil.toSnakeCaseKey(params)
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchCompanyDetail = createAsyncThunk(
  "company/fetchCompanyDetail",
  async (params) => {
    const { companyId } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/detail/${companyId}`,
      method: "GET",
    });
    if (!result) {
      return;
    }
    return result.data;
  }

);

export const fetchCompanyCreateByAdmin = createAsyncThunk(
  "company/fetchCompanyCreateByAdmin",
  async (params) => {
    const { fileLogo, contentData } = params;
    const data = new FormData();
    fileLogo && data.append('file ', fileLogo);
    data.append('content-data', new Blob([JSON.stringify(snakecaseKeys(contentData))], {
      type: 'application/json'
    }));
    const result  = await httpRequest({
      url: `${_urlPrefix}/create`,
      method: 'POST',
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      data: data,
      successMessage: "toast.create.200"
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchUpdateCompanyPersonalInfo = createAsyncThunk(
  "company/fetchUpdateCompanyPersonalInfo",
  async (params, { dispatch }) => {
    const { fileLogo, contentData, hostname, isUpdateDomain, approvalStatus } = params;
    const data = new FormData();
    fileLogo && data.append('file ', fileLogo);
    data.append('content-data', new Blob([JSON.stringify(snakecaseKeys(contentData))], {
      type: 'application/json'
    }));
    const result  = await httpRequest({
      url: `${_urlPrefix}/update/personal-info`,
      method: 'PUT',
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      data: data,
      successMessage: "toast.update.200"
    });
    if (!result) {
      return;
    }
    if (isUpdateDomain && approvalStatus === APPROVAL_STATUS.ACTIVE) {
      const port = window.location.port;
      dispatch(
        showToast({
          type: "success",
          message: t('text.updateDomain'),
          notShowIcon: true
        })
      );
      setTimeout(() => {
        window.open(`${window.location.protocol}//${hostname}${port && port !== '' ? ':' + port : ''}/auth/company/login`, '_self');
        // remove token
        StorageHelper.removeCookie(AppConfig.STORAGE_KEYS.token);
        StorageHelper.removeLocalItem(AppConfig.STORAGE_KEYS.user);
      }, 3000);
    } else if (hostname && hostname !== '') {
      dispatch(fetchCompanyConfig({ domain: hostname }));
    }
    
    return result.data;
  }
);

export const fetchCompanyUpdateVerification = createAsyncThunk(
  "company/fetchCompanyUpdateVerification",
  async (params) => {
    const {
      companyId,
      certificationFile,
      professionalIndemnityInsuranceFile,
      abn,
    } = params;
    const data = new FormData();

    certificationFile && data.append('file ', certificationFile);
    professionalIndemnityInsuranceFile && data.append('professional_indemnity_insurance_file ', professionalIndemnityInsuranceFile);
    data.append('content-data', new Blob([JSON.stringify(snakecaseKeys({
      companyId, abn
    }))], {
      type: 'application/json'
    }));
    const result = await httpRequest({
      url: `${_urlPrefix}/update/verification`,
      method: "PUT",
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      data: data,
      successMessage: "toast.update.200"
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchCompanyUpdateBillingInfo = createAsyncThunk(
  "company/fetchCompanyUpdateBillingInfo",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/update/billing-info`,
      method: "PUT",
      data: AppUtil.toSnakeCaseKey(params),
      successMessage: "toast.update.200"
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchCompanyUpdateAgreement = createAsyncThunk(
  "company/fetchCompanyUpdateAgreement",
  async (params) => {
    const {
      id,
      agreementFile
    } = params;
    const data = new FormData();
    agreementFile && data.append('agreement_file', agreementFile);
    const result = await httpRequest({
      url: `${_urlPrefix}/update/agreement/${id}`,
      method: "PUT",
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      data: data,
      successMessage: "toast.update.200"
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchVerifyCompany = createAsyncThunk(
  "company/fetchVerifyCompany",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/verify-on-board`,
      method: "PUT",
      data: AppUtil.toSnakeCaseKey(params),
      successMessage: "toast.update.200"
    });
    if (!result) {
      return;
    }
    return result.data;
  }
)

export const fetchCompanyFeedback = createAsyncThunk(
  "company/fetchCompanyFeedback",
  async (params) => {
    const { companyId } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/on-board-feedbacks/${companyId}`,
      method: "GET"
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchActiveCompany = createAsyncThunk(
  "company/fetchActiveCompany",
  async (params) => {
    const { companyId } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${companyId}`,
      method: "PUT",
      successMessage: "toast.update.200"
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchDeleteCompany = createAsyncThunk(
  "company/fetchDeleteCompany",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/delete`,
      data: AppUtil.toSnakeCaseKey(params),
      method: "DELETE",
      successMessage: 'toast.remove.200'
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const reduceSignUpCompany = createAsyncThunk(
  "company/reduceSignUpCompany",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/sign-up-id-password`,
      data: AppUtil.toSnakeCaseKey(params),
      method: "POST",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const reduceSignupGoogleCompany = createAsyncThunk(
  "company/reduceSignupGoogleCompany",
  async (params, { dispatch }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/sign-up-google`,
      method: "POST",
      data: AppUtil.toSnakeCaseKey(params),
      successMessage: "toast.signup.200"
    });
    if (!result) {
      return;
    }
    dispatch(saveAuthToken({
      accessToken: result.data.accessToken,
      expireDate: result.data.expireDate
    }));
    return result.data;
  }
);

export const reduceSignupFacebookCompany = createAsyncThunk(
  "company/reduceSignupFacebookCompany",
  async (params, { dispatch }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/sign-up-facebook`,
      method: "POST",
      data: AppUtil.toSnakeCaseKey(params),
      successMessage: "toast.signup.200"
    });
    if (!result) {
      return;
    }
    dispatch(saveAuthToken({
      accessToken: result.data.accessToken,
      expireDate: result.data.expireDate
    }));
    return result.data;
  }
);

export const reduceUpdateCompanyNotifyEmail = createAsyncThunk(
  "company/reduceUpdateCompanyNotifyEmail",
  async (params) => {
      const result = await httpRequest({
          url: `${_urlPrefix}/update/notify-email`,
          data: AppUtil.toSnakeCaseKey(params),
          method: "PUT",
          successMessage: 'toast.update.200'
      });
      if (!result) {
          return;
      }
      return result.data;
  }
);

export const reduceActivateCompany = createAsyncThunk(
  "company/reduceActivateCompany",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/${params}/activate`,
      method: "PUT",
      successMessage: "toast.activate.200"
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const reduceDeActivateCompany = createAsyncThunk(
  "company/reduceDeActivateCompany",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/${params}/deactivate`,
      method: "PUT",
      successMessage: "toast.de_activate.200"
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const reduceDisconnectToAccounting = createAsyncThunk(
  "compnay/reduceDisconnectToAccounting",
  async (params, { dispatch }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/disconnect-accounting-service`,
      method: "DELETE",
      params: params,
      successMessage: "toast.disconnect.200"
    });
    if (!result) {
      return;
    }
    dispatch(getMe());
    return result.data;
  }
);

export const reduceConnectToXero = createAsyncThunk(
  "company/reduceConnectToXero",
  async (params, { dispatch }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/connect-to-xero`,
      method: "GET",
      params: params,
      successMessage: "toast.connect.200"
    });
    if (!result) {
      return;
    }
    dispatch(getMe());
    return result.data;
  }
);

export const reduceConnectToQuickBook = createAsyncThunk(
  "company/reduceConnectToQuickBook",
  async (params, { dispatch }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/connect-to-quickbook`,
      method: 'GET',
      params: params,
      successMessage: "toast.connect.200"
    });
    if (!result) {
      return;
    }
    dispatch(getMe());
    return result.data;
  }
)

export const reduceUpdateCardInfo = createAsyncThunk(
  "company/reduceUpdateCardInfo",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/update/card`,
      method: 'PUT',
      data: AppUtil.toSnakeCaseKey(params),
      successMessage: "toast.update.200"
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchSubscriptionHistories = createAsyncThunk(
  "company/fetchSubscriptionHistories",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/subscription-histories/${params.companyId}`,
      method: 'GET',
      params: AppUtil.toSnakeCaseKey(params.data)
    });
    if (!result) {
      return;
    }
    return result.data;
  }
)

export const fetchCompanyConfig = createAsyncThunk(
  "company/fetchCompanyConfig",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/public/get-config`,
      method: "GET",
      params: AppUtil.toSnakeCaseKey(params)
    });
    if (!result) {
      return;
    }
    return result.data;
  }

);

export const updateAgreementStaff = createAsyncThunk(
  "company/updateAgreementStaff",
  async (params, { dispatch }) => {
    const { file, domain } = params;
    const data = new FormData();

    file && data.append('agreement-file ', file);
    const result = await httpRequest({
      url: `${_urlPrefix}/upload-worker-download-agreement-file`,
      method: "PUT",
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      data: data,
      successMessage: "toast.update.200"
    });
    if (!result) {
      return;
    }
    dispatch(fetchCompanyConfig({ domain: domain }));
    
    return result.data;
  }
);

export const updateAgreementParticipant = createAsyncThunk(
  "company/updateAgreementParticipant",
  async (params, { dispatch }) => {
    const { file, domain } = params;
    const data = new FormData();

    file && data.append('agreement-file ', file);
    const result = await httpRequest({
      url: `${_urlPrefix}/upload-client-download-agreement-file`,
      method: "PUT",
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      data: data,
      successMessage: "toast.update.200"
    });
    if (!result) {
      return;
    }
    dispatch(fetchCompanyConfig({ domain: domain }));
    return result.data;
  }
);

export const updateAgreementStaffTax = createAsyncThunk(
  "company/updateAgreementStaffTax",
  async (params, { dispatch }) => {
    const { file, domain } = params;
    const data = new FormData();

    file && data.append('agreement-tax-file ', file);
    const result = await httpRequest({
      url: `${_urlPrefix}/upload-worker-download-agreement-tax-file`,
      method: "PUT",
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      data: data,
      successMessage: "toast.update.200"
    });
    if (!result) {
      return;
    }
    dispatch(fetchCompanyConfig({ domain: domain }));
    return result.data;
  }
);

const initialState = {
  companyList: [],
  companyDetail: {},
  companyConfig: {},
  companyXero: {
    companyClientId: '',
    companyClientSecret: ''
  }
}

const company = createSlice({
  name: 'company',
  initialState: initialState,
  reducers: {
    clearCompanyDetail: (company) => {
      company.companyDetail = {};
    },
    updateCompanyXero: (state, action) => {
      const { companyClientId, companyClientSecret } = action.payload;
      state.companyXero.companyClientId = companyClientId;
      state.companyXero.companyClientSecret = companyClientSecret;
    }
  },
  extraReducers: {
    [fetchCompanyListPaging.fulfilled]: (state, action) => {
      const { payload } = action;
      if(payload) {
        state.companyList = payload.content;
      }
      return state;
    },
    [fetchCompanyDetail.fulfilled]: (state, action) => {
      const { payload } = action;
      if (payload) {
        let clone = { ...payload };
        for (const key in clone.client) {
          if (!clone.client[key]) {
            clone.client[key] = '';
          }
        }
        for (const key in clone.clientDetail) {
          if (!clone.clientDetail[key]) {
            clone.clientDetail[key] = '';
          }
        }
        state.companyDetail = clone;
      }
      return state;
    },
    [fetchCompanyConfig.fulfilled]: (state, action) => {
      const { payload } = action;
      if (payload) {
        state.companyConfig = { ...payload };
      }
      return state;
    }
  }
});

const { reducer, actions } =company;
export const { clearCompanyDetail, updateCompanyXero } = actions;
export default reducer;

export const getCompanyList = createSelector(
  state => state.company.companyList,
  companyList => companyList
);

export const getCompanyDetail = createSelector(
  state => state.company.companyDetail,
  companyDetail =>  companyDetail
);

export const getCompanyXero = createSelector(
  state => state.company.companyXero,
  companyXero =>  companyXero
);

export const getCompanyConfig = createSelector(
  state => state.company.companyConfig,
  companyConfig =>  companyConfig
);